import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { FormInput } from '../../../../../components/input/index';
import { Company, useLocale } from '../../../../../hooks/index';
import { toNumber } from '../../../../../utils/to-number.util';
import { isTerminatedStakeholderIssuanceRowFrozen } from '../../../utils/terminated-stakeholder.util';
import {
  FormDropDown,
  IssuanceRowStatus,
  ReadOnlyField,
  ReverseVestingFields,
  ShareClassCombobox,
  StakeholderCombobox,
} from '../../components/index';
import { ChildrenFormProps } from '../../event-form.types';
import {
  companyIncorporationIssuanceSchema,
  companyIncorporationStepTwoSchema,
  FormSchema,
  MAX_EVENT_ITEMS,
} from '../../event-form.validation';
import {
  isReverseVestingIncomplete,
  resetReverseVestingOnStakeholderChange,
  resetReverseVestingSharesOnIssuedSharesChange,
} from '../../utils/reverse-vesting-form.util';

export type CompanyIncorporationStepTwoProps = ChildrenFormProps;

export const CompanyIncorporationStepTwo: FC<CompanyIncorporationStepTwoProps> = ({
  control,
  companyId,
  clearErrors,
  setFormData,
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  lockMode,
  isLoading,
}) => {
  const { messagesLocale } = useLocale();
  const { companyIncorporation } = useWatch<FormSchema>({ control });
  const { selectedCompany: { currency } = {} } = Company.useSelected();
  const parValue = companyIncorporation?.parValue ?? '';

  const {
    fields: issuanceFields,
    append: appendIssuance,
    remove: removeIssuance,
  } = useFieldArray({
    control,
    name: 'companyIncorporation.issuances',
  });

  const isValid = companyIncorporationStepTwoSchema.safeParse(companyIncorporation).success;

  const handleAppendIssuance = useCallback(() => {
    appendIssuance({
      stakeholder: { id: '', fullName: '' },
      shareClass: { id: '', name: '' },
      shares: 0,
      reverseVesting: undefined,
    });
  }, [appendIssuance]);

  const getIssuanceTitle = useCallback(
    (index: number) => {
      const stakeholderName = companyIncorporation?.issuances?.[index]?.stakeholder?.fullName;
      return stakeholderName
        ? `${messagesLocale['ISSUANCE']} - ${stakeholderName}`
        : messagesLocale['ISSUANCE'];
    },
    [companyIncorporation?.issuances, messagesLocale],
  );

  /** See the note on the Share Issuance equivalent: the schema, not formState, decides this. */
  const getRowStatus = (index: number) => {
    const row = companyIncorporation?.issuances?.[index];

    return {
      isComplete: companyIncorporationIssuanceSchema.safeParse(row).success,
      isReverseVestingEnabled: Boolean(row?.reverseVesting?.enabled),
      isReverseVestingIncomplete: isReverseVestingIncomplete(row),
      stakeholderName: row?.stakeholder?.fullName || messagesLocale['ISSUANCE'],
    };
  };

  const considerationByIndex = useMemo(() => {
    const parsedParValue = toNumber(parValue);
    return (companyIncorporation?.issuances ?? []).map((issuance) => {
      const shares = Number(issuance?.shares ?? 0);
      if (!parsedParValue || !shares) return 0;
      return parsedParValue * shares;
    });
  }, [companyIncorporation?.issuances, parValue]);

  useEffect(() => {
    if (issuanceFields.length === 0) {
      handleAppendIssuance();
    }
  }, [handleAppendIssuance, issuanceFields.length]);

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        {issuanceFields.map((field, index) => {
          const row = companyIncorporation?.issuances?.[index];
          // Termination settled this row's holdings, so the whole card — details and schedule —
          // is frozen in edit mode. New rows (no id yet) stay editable even for terminated holders.
          const isRowFrozen =
            Boolean(lockMode) && isTerminatedStakeholderIssuanceRowFrozen(row ?? {});

          return (
            <FormDropDown
              handleRemove={() => removeIssuance(index)}
              isOpenOnStart={index === issuanceFields.length - 1}
              key={field.id}
              showRemoveButton={issuanceFields.length > 1 && !isRowFrozen}
              title={getIssuanceTitle(index)}
              titleSlot={<IssuanceRowStatus {...getRowStatus(index)} />}
            >
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full flex-col gap-3">
                  {isRowFrozen ? (
                    <div className="grid w-full grid-cols-2 gap-3">
                      <ReadOnlyField
                        label={<AppFormattedMessage id="STAKEHOLDER" />}
                        value={row?.stakeholder?.fullName}
                      />
                      <ReadOnlyField
                        label={<AppFormattedMessage id="SHARE_CLASS" />}
                        value={row?.shareClass?.name}
                      />
                      <ReadOnlyField
                        label={<AppFormattedMessage id="NO_OF_SHARES" />}
                        value={Number(row?.shares ?? 0).toLocaleString('en-US')}
                      />
                      <ReadOnlyField
                        label={<AppFormattedMessage id="CONSIDERED_PAY" />}
                        value={`${currency?.symbol ?? '$'}${(considerationByIndex[index] ?? 0).toLocaleString('en-US', { maximumFractionDigits: 7 })}`}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm font-[450] text-gray-700">
                          <AppFormattedMessage id="STAKEHOLDER" />
                        </span>
                        <StakeholderCombobox
                          companyId={companyId}
                          control={control}
                          name={`companyIncorporation.issuances.${index}.stakeholder`}
                          onSelectStakeholder={({ id }) =>
                            resetReverseVestingOnStakeholderChange({
                              clearErrors,
                              currentStakeholderId:
                                companyIncorporation?.issuances?.[index]?.stakeholder?.id,
                              namePrefix: `companyIncorporation.issuances.${index}.reverseVesting`,
                              nextStakeholderId: id,
                              setFormData,
                            })
                          }
                        />
                      </div>
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm font-[450] text-gray-700">
                          <AppFormattedMessage id="SHARE_CLASS" />
                        </span>
                        <ShareClassCombobox
                          companyId={companyId}
                          control={control}
                          fieldName={`companyIncorporation.issuances.${index}.shareClass`}
                        />
                      </div>
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm font-[450] text-gray-700">
                          <AppFormattedMessage id="NO_OF_SHARES" />
                        </span>
                        <FormInput
                          control={control}
                          name={`companyIncorporation.issuances.${index}.shares`}
                          numberOnly
                          onChange={(value) =>
                            resetReverseVestingSharesOnIssuedSharesChange({
                              clearErrors,
                              currentIssuedShares: Number(row?.shares ?? 0),
                              namePrefix: `companyIncorporation.issuances.${index}.reverseVesting`,
                              nextIssuedShares: toNumber(value),
                              reverseVesting: row?.reverseVesting,
                              setFormData,
                            })
                          }
                          placeholder={<AppFormattedMessage id="VALUE" />}
                          shouldFormatNumber
                          wrapperClassName="w-[224px]"
                        />
                      </div>
                      <div className="h-[1px] w-full bg-gray-100" />
                      <div className="flex w-full items-center justify-between">
                        <span className="text-sm font-[450] text-gray-700">
                          <AppFormattedMessage id="CONSIDERED_PAY" />
                        </span>
                        <span className="rounded-md bg-brand-25 px-3 py-1 text-sm font-[550] text-brand-700">
                          {`${currency?.symbol ?? '$'}${(considerationByIndex[index] ?? 0).toLocaleString('en-US', { maximumFractionDigits: 7 })}`}
                        </span>
                      </div>
                    </>
                  )}
                  <ReverseVestingFields
                    clearErrors={clearErrors}
                    control={control}
                    disabled={Boolean(row?.stakeholder?.isTerminated)}
                    disabledReason={messagesLocale['REVERSE_VESTING_TERMINATED_STAKEHOLDER']}
                    issuedShares={Number(row?.shares ?? 0)}
                    namePrefix={`companyIncorporation.issuances.${index}.reverseVesting`}
                    setFormData={setFormData}
                  />
                </div>
              </div>
            </FormDropDown>
          );
        })}
        <Button
          className="w-fit px-0 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
          disabled={issuanceFields.length >= MAX_EVENT_ITEMS}
          onClick={handleAppendIssuance}
          styleType="NONE"
        >
          + <AppFormattedMessage id="ADD_ANOTHER_ISSUANCE" />
        </Button>
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
